import { useAuthState, useCurrentUser } from "@/data/user";
import { useProjectServerConfig } from "@/data/servers";
import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import { useAuth } from "@/hooks/use-auth";
import { AlertTriangle } from "lucide-react";
import { toast } from "@/lib/toast";
import { ServersTab } from "./components/ServersTab";
import { ToolsTab } from "./components/ToolsTab";
import { ResourcesTab } from "./components/ResourcesTab";
import { PromptsTab } from "./components/PromptsTab";
import { SkillsTab } from "./components/SkillsTab";
import { TasksTab } from "./components/TasksTab";
import { ActiveHostCapsResolverScope } from "./contexts/active-host-client-capabilities-context";
import type { EvalChatHandoff } from "./lib/eval-chat-handoff";
import { EvalsTab } from "./components/EvalsTab";
import {
  useProjectQueries,
} from "./hooks/useProjects";
import { SettingsTab } from "./components/SettingsTab";
import { ProjectClientConfigSync } from "./components/client-config/ProjectClientConfigSync";
import { ActiveHostServerReconciler } from "./components/ActiveHostServerReconciler";
import { TracingTab } from "./components/TracingTab";
import { OAuthFlowTab } from "./components/OAuthFlowTab";
import { ErrorBoundary } from "./components/ui/error-boundary";
import { PlaygroundTab } from "./components/playground/PlaygroundTab";
import { EXCALIDRAW_SERVER_NAME } from "./lib/excalidraw-quick-connect";
import { isFirstRunEligible } from "./lib/onboarding-state";
import { HostsTab } from "./components/HostsTab";
import { QueuePage } from "./components/queue/QueuePage";
import { HostConfigCompareView } from "./components/hosts/comparison/HostConfigCompareView";
import { CaniuseCapabilityPage } from "./components/hosts/comparison/CaniuseCapabilityPage";
import { HostSectionTabs } from "./components/hosts/HostSectionTabs";
import { ConnectViewHeader } from "./components/hosts/ConnectViewHeader";
import { ClientConnectorsHeader } from "./components/hosts/ClientConnectorsHeader";
import { useComputersEnabledState } from "./hooks/useComputersEnabled";
import { useSkillsEnabledState } from "./hooks/useSkillsEnabled";
import { motion } from "framer-motion";
import { SNAPPY_RAIL } from "./components/hosts/transition-tokens";
import OAuthDebugCallback from "./components/oauth/OAuthDebugCallback";
import OAuthDesktopReturnNotice from "./components/oauth/OAuthDesktopReturnNotice";
import { MCPSidebar } from "./components/mcp-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "./components/ui/sidebar";
import { Button } from "@sphinx/design-system/button";
import { useAppState, type ServerWithName } from "./hooks/use-app-state";
import { useActorKey } from "./hooks/use-actor-key";
import {
  PreferencesStoreProvider,
  usePreferencesStore,
} from "./stores/preferences/preferences-provider";
import { Toaster } from "@sphinx/design-system/sonner";
import { useElectronOAuth } from "./hooks/useElectronOAuth";
import {
  useHiddenHeaderServers,
  type HeaderSurface,
} from "./hooks/useHiddenHeaderServers";
import { hasDebuggerHeaderServers } from "./lib/debugger-header-servers";
import { AppStateProvider } from "./state/app-state-context";
import { ServerActionsProvider } from "./state/server-actions-context";
import { usePreviewedHostId } from "./hooks/use-previewed-client-id";
import { useOrganizationQueries } from "./hooks/useOrganizations";

// Import global styles
import "./index.css";
import { track } from "./lib/analytics";
import {
  getInitialThemeMode,
  updateThemeMode,
  getInitialThemePreset,
  updateThemePreset,
} from "./lib/theme-utils";
import LoadingScreen from "./components/LoadingScreen";
import { Header } from "./components/Header";
import { ThemePreset } from "./types/preferences/theme";
import type {
  ActiveServerSelectorProps,
  PlaygroundServerSelectorProps,
} from "./components/ActiveServerSelector";
import { useProjectServers } from "./hooks/useViews";
import { useApiContext } from "./hooks/hosted/use-hosted-api-context";
import { useHostedClientCapabilities } from "./hooks/hosted/use-hosted-client-capabilities";
import { useLocalStateMigration } from "./hooks/use-local-state-migration";
import { AppReadyProvider } from "./hooks/use-app-ready";
import { useInspectorCommandBus } from "./hooks/use-inspector-command-bus";
import {
  driveChatScopeStepUp,
  resolveScopeStepUpServer,
} from "./lib/scope-step-up";
import { markPendingChatScopeStepUpCancelled } from "./lib/scope-step-up-pending";
import { HOSTED_MODE } from "./lib/config";
import {
  createInspectorCommandClientError,
  registerInspectorCommandHandler,
} from "./lib/inspector-command-handlers";
import { waitForUiCommit } from "./lib/wait-for-ui-commit";
import { subscribeToOAuthDebuggerRequests } from "./lib/oauth/oauth-debugger-navigation";
import {
  clearProjectDeepLinkFromUrl,
  hasProjectDeepLinkParam,
  readProjectDeepLinkParam,
  resolveProjectDeepLinkAction,
} from "./lib/project-deep-link";
import {
  isHostedTabBlocked,
  normalizeHostedHashTab,
} from "./lib/hosted-tab-policy";
import { buildOAuthTokensByServerId } from "./lib/oauth/oauth-tokens";
import type { OAuthTrace } from "./lib/oauth/oauth-trace";
import { getNewlyConnectedServers } from "./lib/connected-server-auto-open";
import {
  clearHostedOAuthPendingState,
  getHostedOAuthCallbackContext,
  resolveHostedOAuthReturnPath,
} from "./lib/hosted-oauth-callback";
import {
  clearScenarioSignInReturnPath,
  readScenarioSignInReturnPath,
} from "./lib/scenario-session";
import {
  clearCliSignInReturnPath,
  readCliSignInReturnPath,
} from "./lib/cli-signin-return-path";
import {
  sanitizeHostedOAuthErrorMessage,
  clearHostedOAuthResumeMarker,
  writeHostedOAuthResumeMarker,
} from "./lib/hosted-oauth-resume";
import {
  completeHostedOAuthCallback,
  handleOAuthCallback,
  OAUTH_PENDING_STORAGE_KEY,
} from "./lib/oauth/mcp-oauth";
import {
  buildElectronMcpCallbackUrl,
  resolveEffectiveWireProtocolVersion,
} from "./hooks/use-server-state";
import {
  isKnownProtocolVersion,
  readTasksPolicy,
  readXaaEnterprisePolicy,
  taskModeForSurface,
  type McpProtocolVersion,
} from "@sphinx/sdk/browser";
import { gateMcpToolResultImageRenderingByModelVisibility } from "./lib/client-config-v2";
import type { ProjectServerConfigDto } from "./lib/project-server-config";
import { shouldQueryHostId, useHostList } from "@/hooks/useClients";
import {
  HOST_TEMPLATES,
  type HostTemplateId,
} from "@sphinx/sdk/host-config/templates";
import {
  HOST_VERIFY_TAB_PARAM,
  HOST_VERIFY_TEMPLATE_PARAM,
  hostFocusTabToVerifyParam,
  parseHostVerifyTabParam,
} from "./components/hosts/host-verify-deep-link";
import type { HostFocusTabId } from "./components/hosts/redesigned/types";
import {
  buildHostsPath,
  isDebugOAuthCallbackPath,
  isKnownAppTabSegment,
  listKnownAppTabSegments,
  navigateApp,
  navigationTargetToPath,
  pathnameToActiveTab,
  routePaths,
  shouldSnapToServersOnActiveProjectChange,
  useActiveTab,
  useAppNavigate,
} from "./lib/app-navigation";
import {
  Navigate,
  Outlet,
  UNSAFE_LocationContext,
  useLocation,
  useOutletContext,
  useParams,
} from "react-router";
import { useProjectClientConfigSyncPending } from "./hooks/use-project-client-config-sync-pending";
import { ingestOAuthTraceLogs } from "./stores/traffic-log-store";
import type {
  ConnectServerInspectorCommand,
  DisconnectServerInspectorCommand,
  NavigateInspectorCommand,
  OpenPlaygroundInspectorCommand,
  RemoveServerInspectorCommand,
  SelectServerInspectorCommand,
  SnapshotAppInspectorCommand,
} from "@/shared/inspector-command.js";
import { sanitizeTraceErrorMessage } from "./lib/oauth/trace-redaction";

// Accounts created on/after this ship date are treated as "new" for the
// first-run Playground redirect. Older accounts (created before the cutoff)
// are never redirected, regardless of their onboarding flag, so returning
// users always keep the Home landing.
const FIRST_RUN_PLAYGROUND_ROLLOUT_MS = Date.parse("2026-06-16T00:00:00.000Z");

function getHostedOAuthCallbackErrorMessage(): string {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  const description = params.get("error_description");

  if (error === "access_denied" && !description) {
    return "Authorization was cancelled. Try again.";
  }

  return sanitizeHostedOAuthErrorMessage(
    description || error,
    "Authorization could not be completed. Try again."
  );
}

function clearHostedCallbackRetryState() {
  clearHostedOAuthPendingState();
  clearHostedOAuthResumeMarker();
  localStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
  localStorage.removeItem("mcp-oauth-return-hash");

  for (const storage of [window.localStorage, window.sessionStorage]) {
    const workosKeys: string[] = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && /workos/i.test(key)) {
        workosKeys.push(key);
      }
    }

    for (const key of workosKeys) {
      storage.removeItem(key);
    }
  }
}

/**
 * Redact the OAuth debugger's error-boundary output.
 *
 * Uses the SDK's single trace redactor rather than a local pattern list — this
 * used to be a fourth private copy, and a private copy is how the sensitive-
 * field set drifts.
 *
 * The stack is redacted in ONE pass with a raised cap, not line by line. A
 * multi-line payload can put a JSON credential's key and its value on separate
 * lines, and splitting first hands the redactor two fragments that match
 * neither — so the value survives into copied and reported output. The cap
 * exists for a single error message; a stack legitimately needs more room.
 */
const MAX_REDACTED_STACK = 8_000;

function redactStackLikeText(value: string | null | undefined): string {
  if (!value) return "";
  return sanitizeTraceErrorMessage(value, {
    maxLength: MAX_REDACTED_STACK,
    maxScanned: MAX_REDACTED_STACK * 2,
  });
}

function redactOAuthDebuggerError(error: Error | null) {
  return {
    name: sanitizeTraceErrorMessage(error?.name ?? "Error"),
    message: sanitizeTraceErrorMessage(error?.message ?? "Unknown error"),
    stack: redactStackLikeText(error?.stack),
  };
}

function formatOAuthDebuggerErrorDetails(error: Error | null): string {
  const sanitized = redactOAuthDebuggerError(error);
  return [
    "OAuth Debugger error",
    `Name: ${sanitized.name}`,
    `Message: ${sanitized.message}`,
    sanitized.stack ? `Stack:\n${sanitized.stack}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
}

function UserSetupError() {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
      data-testid="user-setup-error"
    >
      <div className="max-w-md space-y-2">
        <h1 className="text-xl font-semibold">Could not finish setup</h1>
        <p className="text-sm text-muted-foreground">
          We could not create your Sphinx user record. Refresh and try again.
        </p>
      </div>
      <button
        type="button"
        className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        onClick={() => window.location.reload()}
      >
        Refresh
      </button>
    </div>
  );
}

type AppChromeSidebarProps = ComponentProps<typeof MCPSidebar> & {
  hidden: boolean;
};

function AppChromeSidebar({ hidden, ...props }: AppChromeSidebarProps) {
  if (hidden) {
    return null;
  }

  return <MCPSidebar {...props} />;
}

type AppChromeHeaderProps = ComponentProps<typeof Header> & {
  hidden: boolean;
};

function AppChromeHeader({ hidden, ...props }: AppChromeHeaderProps) {
  const { isMobile } = useSidebar();
  if (hidden && !isMobile) {
    return null;
  }

  return <Header {...props} />;
}


type AppRouteContext = Record<string, any>;

const AppRouteReactContext = createContext<AppRouteContext | null>(null);

function useAppRouteContext() {
  const context = useContext(AppRouteReactContext);
  return context ?? useOutletContext<AppRouteContext>();
}

/**
 * The no-router render path.
 *
 * REACHABLE — it is what renders when the app runs without React Router
 * (App.tsx mounts it twice). It is easy to assume otherwise because the router
 * table in `router.tsx` looks like the only route map, which is exactly why the
 * two had drifted: the router handles the legacy `chat` and `client-config`
 * aliases and this switch did not, so those two tab ids fell through to
 * `default` and rendered Servers.
 *
 * `client-config` happened to be harmless (Servers IS its redirect target);
 * `chat` was not — it rendered Servers while the sidebar showed Playground.
 * Both are explicit arms now, pointing at the same destinations `router.tsx`
 * uses. When you add a route there, add it here.
 */
function NoRouterRouteBody({ activeTab }: { activeTab: string }) {
  switch (activeTab) {
    // Legacy aliases, mirroring router.tsx's ChatAliasRoute /
    // ServersRedirectRoute. A navigate-away effect also fires for these; the
    // arm is what they render in the meantime, so it must match where they
    // are going.
    case "chat":
      return <PlaygroundRoute />;
    case "client-config":
      return <ServersRoute />;
    case "tools":
      return <ToolsRoute />;
    case "resources":
      return <ResourcesRoute />;
    case "prompts":
      return <PromptsRoute />;
    case "tasks":
      return <TasksRoute />;
    case "skills":
      return <SkillsRoute />;
    case "oauth-flow":
      return <OAuthFlowRoute />;
    case "tracing":
      return <TracingRoute />;
    // `clients` is a segment of the Client Connectors surface now: bare
    // `/clients` redirects here, and only `/clients/:hostId` (the per-client
    // config canvas) renders HostsRoute — which this switch cannot express,
    // since it sees a tab id and not a path.
    case "clients":
      return <ServersRoute />;
    case "host-compare":
      return <HostCompareRoute />;
    case "playground":
      return <PlaygroundRoute />;
    case "settings":
      return <SettingsRoute />;
    case "evals":
      return <EvalsRoute />;
    case "queue":
      return <QueueRoute />;
    case "connectors":
    default:
      return <ServersRoute />;
  }
}

export function ServersRoute() {
  const { convexProjectId, isAuthenticated } = useAppRouteContext();
  const navigate = useAppNavigate();

  // From /connectors, "select a host" means navigate to /clients/:id. State sync
  // happens in HostsRoute via the URL → hostsTabSelectedHostId effect, so
  // here we only need to drive the URL.
  const handleSelectHost = useCallback(
    (next: string | null) => {
      navigate(next ? buildHostsPath(next) : routePaths.connectors);
    },
    [navigate]
  );

  // `/clients?template=claude` (the caniuse "Verify against your server" link)
  // now redirects here with its query string intact, so the deep link has to
  // resolve on this route rather than on the deleted Clients page.
  useTemplateVerifyDeepLink({
    isAuthenticated,
    projectId: convexProjectId,
    navigate,
  });

  // Local mode: the Connect switcher is the only path to Skills, so it must
  // render regardless of auth/guest/project state. HostsTab (which owns the
  // header when a cloud project exists) bails to a bare view without a
  // projectId, so wrap ServersTabBody ourselves in both degraded states.
  if (!HOSTED_MODE && (!isAuthenticated || !convexProjectId)) {
    return (
      <motion.div
        key="servers-local"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={SNAPPY_RAIL}
        className="flex h-full min-h-0 flex-col"
      >
        <ClientConnectorsHeader
          projectId={convexProjectId ?? null}
          isAuthenticated={isAuthenticated}
          view="servers"
          onOpenConnectors={() => navigate(routePaths.connectors)}
          onOpenClientConfig={(hostId) => navigate(buildHostsPath(hostId))}
          onOpenSkills={() => navigate(routePaths.skills)}
        />
        <div className="min-h-0 flex-1">
          <ServersTabBody />
        </div>
      </motion.div>
    );
  }

  if (!isAuthenticated) {
    return <ServersTabBody />;
  }

  return (
    <HostsTab
      projectId={convexProjectId}
      isAuthenticated={isAuthenticated}
      selectedHostId={null}
      onSelectHost={handleSelectHost}
      serversTabElement={<ServersTabBody />}
    />
  );
}

function ServersTabBody() {
  const {
    projectServers,
    handleConnect,
    handleDisconnect,
    handleReconnect,
    handleUpdate,
    handleRemoveServer,
    saveServerConfigWithoutConnecting,
    projects,
    activeProjectId,
    pendingDashboardOAuth,
    isAuthLoading,
    isLoadingRemoteProjects,
    areServersHydrated,
    isWorkOsLoading,
    handleProjectShared,
    handleLeaveProject,
  } = useAppRouteContext();

  return (
    <ServersTab
      projectServers={projectServers}
      onConnect={handleConnect}
      onDisconnect={handleDisconnect}
      onReconnect={handleReconnect}
      onUpdate={handleUpdate}
      onRemove={handleRemoveServer}
      onSaveServerConfig={saveServerConfigWithoutConnecting}
      projects={projects}
      activeProjectId={activeProjectId}
      pendingDashboardOAuth={pendingDashboardOAuth}
      isAuthHydrating={isWorkOsLoading || isAuthLoading}
      isLoadingProjects={isLoadingRemoteProjects}
      areServersHydrated={areServersHydrated}
      onProjectShared={handleProjectShared}
      onLeaveProject={() => handleLeaveProject(activeProjectId)}
    />
  );
}

export function HostsRoute() {
  const {
    convexProjectId,
    hostsTabSelectedHostId,
    isAuthenticated,
    setHostsTabSelectedHostId,
  } = useAppRouteContext();
  const [previewedHostId, setPreviewedHostId] =
    usePreviewedHostId(convexProjectId);
  const params = useParams<{ hostId?: string }>();
  const navigate = useAppNavigate();
  const routeHostId =
    params.hostId ??
    (typeof window !== "undefined" &&
    window.location.pathname.startsWith(`${routePaths.clients}/`)
      ? window.location.pathname
          .slice(`${routePaths.clients}/`.length)
          .split("/")[0]
      : null);
  const urlHostId = useMemo(() => {
    if (!routeHostId) return null;
    let decoded = routeHostId;
    try {
      decoded = decodeURIComponent(routeHostId);
    } catch {
      // Malformed escape: fall through with the raw segment.
    }
    // Trimmed HERE so one canonical id reaches every consumer. `useHost` trims
    // before querying, so a padded `/clients/%20<id>%20` would otherwise resolve
    // the host while the synced and PERSISTED value kept its whitespace — and
    // `HostsTab` reconciles both against the host list, so it would bounce the
    // user to the list and clear the project's previewed host.
    const trimmed = decoded.trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [routeHostId]);
  // Only a Convex document id may be opened, synced, or persisted. Typed and
  // shared links put clients-catalog slugs here too (`/clients/chatgpt`, whose
  // supported deep link is `/clients?template=chatgpt`), and such a value can
  // never resolve to a host. `useHost` skips it; what this adds is keeping it
  // out of the project's PERSISTED previewed host, where it would outlive the
  // URL and reopen a dead id on every later visit to `/clients`.
  //
  // This gate is SHAPE only, so it settles with no host list at all — which is
  // what separates it from the liveness gate below. A slug never resolves for
  // any project, so it is rejected on the first render rather than waiting out
  // a query that would fail argument validation anyway.
  const idShapedHostId =
    urlHostId && shouldQueryHostId(urlHostId) ? urlHostId : null;

  useEffect(() => {
    // Keyed off the raw segment, so `/clients/%20` (nothing left after trimming)
    // is cleaned up too rather than sitting on a URL that opens nothing.
    // No toast: a slug was never a client of theirs, so "no longer exists"
    // (what a dead permalink says below) would be telling them the wrong story.
    if (routeHostId && !idShapedHostId) {
      navigate(routePaths.connectors, { replace: true });
    }
  }, [routeHostId, idShapedHostId, navigate]);

  const { hosts: routeHosts, isLoading: isRouteHostListLoading } = useHostList({
    isAuthenticated,
    projectId: convexProjectId,
  });

  // Where the URL's id stands against the project's client list. Client URLs
  // are permalinks, so bookmarks and history outlive the client itself, and
  // `dead` — deleted, or a link from a project this session isn't in — is
  // reachable from ordinary navigation.
  //
  // `pending` is a real state, not a rounding of `dead`: the list is empty for
  // a beat on every cold start (and `useHostList` also reports loading while
  // signed out, or while `convexProjectId` is still a placeholder, because it
  // skips the query in both cases). Calling an id dead in that window would
  // break every working deep link.
  //
  // Reads `idShapedHostId`, not the raw URL id: a non-id has already been sent
  // to `/clients` by the shape gate above, and routing it through here too would
  // stall it in `pending` on a cold start and then fire the dead-permalink
  // toast at someone who never had that client.
  const urlHostState: "none" | "pending" | "live" | "dead" =
    idShapedHostId === null
      ? "none"
      : isRouteHostListLoading
      ? "pending"
      : routeHosts.some((h) => h.hostId === idShapedHostId)
      ? "live"
      : "dead";

  // The id the canvas may open. A dead id resolves to null HERE, before it
  // reaches shared state, which is what keeps this route out of a fight with
  // `HostsTab`: that component reconciles selection and the previewed host
  // against the same list and clears anything missing, so a route that kept
  // re-syncing the dead id from the URL would have it cleared and re-set on
  // every pass. The corrective navigation below is a React Router transition,
  // and the resulting stream of higher-priority state updates starves it — the
  // canvas never lands on `/clients`, the loop never ends, and it surfaces either
  // as a pegged CPU core or, when the updates chain synchronously, as "Maximum
  // update depth exceeded" (INSPECTOR-CLIENT-224).
  //
  // A `pending` id stays openable so a legitimate permalink opens its canvas
  // immediately rather than flashing the client list for the length of the
  // query. That optimism is safe because it never leaves memory — see the
  // persistence rule below.
  const openableHostId = urlHostState === "dead" ? null : idShapedHostId;

  // Only a CONFIRMED id is written to the project's previewed client, because
  // that store is on disk and outlives the URL that seeded it. Persisting a
  // `pending` id would file an unverified — possibly deleted — client as the
  // project's preview, and closing the tab mid-load would leave it there to be
  // reopened on the next visit.
  const persistableHostId = urlHostState === "live" ? idShapedHostId : null;

  // Bounce a dead permalink to Client Connectors, so the user lands on the
  // merged page (with its client navigator) instead of on a canvas stuck
  // rendering skeletons for a client that isn't there.
  //
  // `replace`, so the dead URL leaves the history stack: a plain push would
  // leave Back pointing at it, and the bounce would repeat on every Back.
  // Keyed by id so it fires once per arrival rather than once per render —
  // and CLEARED whenever the route isn't sitting on a dead id, because this
  // component stays mounted across `/clients` ↔ `/clients/:hostId` (both paths
  // render it, so React reuses the instance and the ref survives). Without the
  // reset, returning to a dead id already bounced once this session would skip
  // both the redirect and the toast, stranding the user on the dead URL.
  const bouncedDeadHostIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (urlHostState !== "dead" || !idShapedHostId) {
      bouncedDeadHostIdRef.current = null;
      return;
    }
    if (bouncedDeadHostIdRef.current === idShapedHostId) return;
    bouncedDeadHostIdRef.current = idShapedHostId;
    navigate(routePaths.connectors, { replace: true });
    toast.error("That client no longer exists. It may have been deleted.");
  }, [urlHostState, idShapedHostId, navigate]);

  // URL is the source of truth for the open host canvas. Sync into shared
  // state so `GlobalHostBar`, `onCanvasReplaceHost`, and other surfaces that
  // still read `hostsTabSelectedHostId` stay aligned.
  useEffect(() => {
    if (hostsTabSelectedHostId !== openableHostId) {
      setHostsTabSelectedHostId(openableHostId);
    }
    if (persistableHostId && previewedHostId !== persistableHostId) {
      setPreviewedHostId(persistableHostId);
    }
  }, [
    openableHostId,
    persistableHostId,
    hostsTabSelectedHostId,
    previewedHostId,
    setHostsTabSelectedHostId,
    setPreviewedHostId,
  ]);

  const handleSelectHost = useCallback(
    (next: string | null) => {
      navigate(next ? buildHostsPath(next) : routePaths.connectors);
    },
    [navigate]
  );

  useTemplateVerifyDeepLink({
    isAuthenticated,
    projectId: convexProjectId,
    navigate,
  });

  if (!isAuthenticated) {
    return <ServersTabBody />;
  }

  // Bare `/clients` is no longer a page of its own: Clients and Connectors are
  // one surface now (Client Connectors, `/connectors`), whose header IS the
  // client navigator. `/clients/:hostId` survives as the config canvas — the
  // page only offers it for the two direct-API clients, but the permalink
  // still opens any of them.
  //
  // Nothing to render rather than a `<Navigate>`: whatever put us here with no
  // openable id — a slug, a dead permalink — has already fired its corrective
  // navigation in an effect above, and a second one would push a duplicate
  // history entry (and drag a Router dependency into a component that is
  // mounted without one in the no-router render path).
  if (!openableHostId) {
    return null;
  }

  return (
    <HostsTab
      projectId={convexProjectId}
      isAuthenticated={isAuthenticated}
      selectedHostId={openableHostId}
      onSelectHost={handleSelectHost}
      serversTabElement={<ServersTabBody />}
    />
  );
}

/**
 * "Verify against your server" deep-link from the public caniuse surface.
 * `/clients?template=claude` opens the matching target's canvas (matched by
 * name) and navigates to `/clients/:hostId`, which drops the query param; with
 * no match it lands on the Targets page. Runs once per mount.
 */
function useTemplateVerifyDeepLink({
  isAuthenticated,
  projectId,
  navigate,
}: {
  isAuthenticated: boolean;
  projectId: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}) {
  const { hosts, isLoading: hostsLoading } = useHostList({
    isAuthenticated,
    projectId,
  });
  const requestedTemplateId = useMemo<HostTemplateId | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get(
      HOST_VERIFY_TEMPLATE_PARAM
    );
    if (!raw) return null;
    return HOST_TEMPLATES.some((t) => t.id === raw)
      ? (raw as HostTemplateId)
      : null;
  }, []);
  const requestedFocusTab = useMemo<HostFocusTabId | null>(() => {
    if (typeof window === "undefined") return null;
    return parseHostVerifyTabParam(window.location.search);
  }, []);
  const handledRef = useRef(false);

  useEffect(() => {
    if (!requestedTemplateId || !isAuthenticated || handledRef.current) return;
    // Wait for the host list before deciding create-vs-open. `useHostList`
    // stays loading while `projectId` is still a placeholder, so this also
    // guards `createHost` from firing with a not-yet-real project id.
    if (hostsLoading) return;
    const template = HOST_TEMPLATES.find((t) => t.id === requestedTemplateId);
    if (!template) return;
    handledRef.current = true;

    const existing = hosts.find((h) => h.name === template.label);
    if (existing) {
      navigate(buildHostVerifyLandingPath(existing.hostId, requestedFocusTab), {
        replace: true,
      });
      return;
    }

    // The target matrix is fixed — a template with no matching target lands
    // on Client Connectors instead of minting a host.
    navigate(routePaths.connectors, { replace: true });
  }, [
    requestedTemplateId,
    isAuthenticated,
    hostsLoading,
    hosts,
    projectId,
    requestedFocusTab,
    navigate,
  ]);
}

/**
 * `?project=<id>` deep-link: shared eval/suite/run URLs carry no project
 * segment, so a link renders whatever project the viewer's picker is on.
 * Surfaces that mass-produce links (the Slack bot, CLI run URLs) append the
 * param; this switches the active project (and organization, when the link
 * crosses orgs) to match, then strips the param. Runs once per mount.
 */
function useProjectDeepLinkSwitch({
  isAuthenticated,
  isLoadingRemoteProjects,
  projects,
  activeProjectId,
  activeOrganizationId,
  setActiveOrganizationId,
  handleSwitchProject,
}: {
  isAuthenticated: boolean;
  isLoadingRemoteProjects: boolean;
  projects: Record<string, unknown>;
  activeProjectId: string | null;
  activeOrganizationId: string | undefined;
  setActiveOrganizationId: (organizationId: string | undefined) => void;
  handleSwitchProject: (projectId: string) => Promise<void>;
}) {
  const requestedProjectId = useMemo(() => {
    if (typeof window === "undefined") return null;
    return readProjectDeepLinkParam(window.location.search);
  }, []);
  // Unfiltered membership list (same Convex query the org-filtered project
  // list derives from, so this dedupes) — needed to resolve cross-org links.
  const { allProjects } = useProjectQueries({ isAuthenticated });
  const handledRef = useRef(false);

  useEffect(() => {
    if (!requestedProjectId || handledRef.current) return;
    if (!isAuthenticated || isLoadingRemoteProjects) return;

    const action = resolveProjectDeepLinkAction({
      requestedProjectId,
      activeProjectId,
      activeOrgProjectIds: new Set(Object.keys(projects)),
      allProjects,
      activeOrganizationId,
    });
    switch (action.kind) {
      case "wait":
        return;
      case "clear":
        handledRef.current = true;
        clearProjectDeepLinkFromUrl();
        return;
      case "switch-organization":
        // The org-filtered project list re-derives; a later run of this
        // effect lands on switch-project. handledRef stays unset on purpose.
        setActiveOrganizationId(action.organizationId);
        return;
      case "switch-project":
        handledRef.current = true;
        // .catch BEFORE .finally: finally re-throws rejections, so a bare
        // .finally chain would turn a failed switch into an unhandled
        // rejection with the user silently left on the wrong project.
        void handleSwitchProject(requestedProjectId)
          .catch(() => {
            toast.error(
              "Couldn't switch to the linked project — use the project picker."
            );
          })
          .finally(clearProjectDeepLinkFromUrl);
        return;
      case "not-found":
        handledRef.current = true;
        clearProjectDeepLinkFromUrl();
        toast.error("This link points to a project you don't have access to.");
        return;
    }
  }, [
    requestedProjectId,
    isAuthenticated,
    isLoadingRemoteProjects,
    projects,
    activeProjectId,
    activeOrganizationId,
    allProjects,
    setActiveOrganizationId,
    handleSwitchProject,
  ]);
}

function buildHostVerifyLandingPath(
  hostId: string,
  tab: HostFocusTabId | null
): string {
  const path = buildHostsPath(hostId);
  if (!tab) return path;
  const tabParam = hostFocusTabToVerifyParam(tab);
  if (!tabParam) return path;
  const params = new URLSearchParams({ [HOST_VERIFY_TAB_PARAM]: tabParam });
  return `${path}?${params.toString()}`;
}

export function HostCompareRoute({ bare = false }: { bare?: boolean } = {}) {
  const { convexProjectId, isAuthenticated } = useAppRouteContext();
  const [previewedHostId] = usePreviewedHostId(convexProjectId);
  const navigate = useAppNavigate();

  const compareView = (
    <HostConfigCompareView
      projectId={convexProjectId}
      isAuthenticated={isAuthenticated}
      presetOnly={bare}
    />
  );

  // Mirror the gating HostsRoute uses: when signed out, Compare has no peer
  // Servers/Client tabs to switch to, so render bare. `bare` forces the same
  // for the chrome-less embed route (caniuse.dev) regardless of auth.
  if (bare || !isAuthenticated) {
    return compareView;
  }

  return (
    <motion.div
      key="host-compare"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={SNAPPY_RAIL}
      className="flex h-full min-h-0 flex-col"
    >
      <ConnectViewHeader
        value="host"
        previewedHostId={previewedHostId}
        onChange={(next) => {
          if (next === "servers") {
            navigate(routePaths.connectors);
          } else if (next === "host" && previewedHostId) {
            navigate(buildHostsPath(previewedHostId));
          } else if (next === "skills") {
            navigate(routePaths.skills);
          }
        }}
        rightSlot={
          // Host/Compare sub-nav inline in the header row (single bar) rather
          // than stacked beneath the primary nav.
          <div className="flex min-w-0 flex-wrap items-center justify-center gap-2 @2xl:justify-end">
            <HostSectionTabs
              value="compare"
              hostEnabled={Boolean(previewedHostId)}
              onSelect={(next) => {
                if (next === "host" && previewedHostId) {
                  navigate(buildHostsPath(previewedHostId));
                }
              }}
            />
          </div>
        }
      />
      <div className="min-h-0 flex-1">{compareView}</div>
    </motion.div>
  );
}

export function CaniuseCapabilityRoute() {
  const params = useParams<{ capabilitySlug?: string }>();
  return <CaniuseCapabilityPage capabilitySlug={params.capabilitySlug} />;
}

export function ToolsRoute() {
  const { selectedMCPConfig, selectedServerEntry, appState, activeHost } =
    useAppRouteContext();
  const prefHostStyle = usePreferencesStore((state) => state.hostStyle);
  const hostStyle = activeHost?.hostStyle ?? prefHostStyle;
  return (
    <ActiveHostCapsResolverScope activeHost={activeHost} hostStyle={hostStyle}>
      <div className="h-full overflow-hidden">
        <ToolsTab
          serverConfig={selectedMCPConfig}
          serverName={appState.selectedServer}
          server={selectedServerEntry ?? undefined}
          serverConnectionStatus={
            selectedServerEntry?.connectionStatus ?? "disconnected"
          }
          tasksMode={taskModeForSurface(
            readTasksPolicy(activeHost?.config),
            "tools"
          )}
          mcpToolResultImageRendering={gateMcpToolResultImageRenderingByModelVisibility(
            activeHost?.config?.mcpToolResultImageRendering,
            activeHost?.config?.modelVisibleMcpToolResults
          )}
        />
      </div>
    </ActiveHostCapsResolverScope>
  );
}

export function EvalsRoute() {
  const {
    convexProjectId,
    ensureServersReady,
    handleContinueEvalInChat,
    handleConnect,
  } = useAppRouteContext();

  return (
    <EvalsTab
      projectId={convexProjectId}
      ensureServersReady={ensureServersReady}
      onContinueInChat={handleContinueEvalInChat}
      handleConnect={handleConnect}
    />
  );
}

// sphinx: runner fleet + job table. Self-contained — reads /api/sphinx directly.
export function QueueRoute() {
  return <QueuePage />;
}

export function ResourcesRoute() {
  const { selectedMCPConfig, selectedServerEntry, appState } =
    useAppRouteContext();
  return (
    <div className="h-full overflow-hidden">
      <ResourcesTab
        serverConfig={selectedMCPConfig}
        serverName={appState.selectedServer}
        server={selectedServerEntry ?? undefined}
        serverConnectionStatus={
          selectedServerEntry?.connectionStatus ?? "disconnected"
        }
      />
    </div>
  );
}

export function PromptsRoute() {
  const { selectedMCPConfig, selectedServerEntry, appState } =
    useAppRouteContext();
  return (
    <div className="h-full overflow-hidden">
      <PromptsTab
        serverConfig={selectedMCPConfig}
        serverName={appState.selectedServer}
        server={selectedServerEntry ?? undefined}
        serverConnectionStatus={
          selectedServerEntry?.connectionStatus ?? "disconnected"
        }
      />
    </div>
  );
}

export function SkillsRoute() {
  const { convexProjectId, isAuthenticated, isGuestProjectActor, appState } =
    useAppRouteContext();
  const servers = appState?.servers as
    | Record<string, ServerWithName>
    | undefined;
  // Memoized on a stable signature rather than rebuilt per render: the
  // consuming section fetches per connection, and a fresh array identity on
  // every render would restart those fetches indefinitely.
  const skillsMcpServers = useMemo(
    () =>
      Object.entries(servers ?? {}).map(([name, server]) => ({
        serverId: name,
        label: name,
        connected: server.connectionStatus === "connected",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // JSON, not concatenation: a server NAME may contain the separators, so
      // `a|b` + `c` and `a` + `b|c` would key the same and the memo would hand
      // back a stale list for a genuinely different set of servers.
      JSON.stringify(
        Object.entries(servers ?? {}).map(([name, server]) => [
          name,
          server.connectionStatus,
        ])
      ),
    ]
  );
  const [previewedHostId] = usePreviewedHostId(convexProjectId);
  const navigate = useAppNavigate();
  const computersEnabled = useComputersEnabledState();
  const skillsEnabled = useSkillsEnabledState();

  // Skills is a Servers-page view (Servers | Client | Computer | Skills), so
  // it renders the same chrome as its peers. Anonymous guests are provisioned
  // Convex actors (`isAuthenticated === true`), so member-ness — not raw auth —
  // decides whether there are peer tabs to switch to (mirrors ComputerRoute).
  const isSignedInMember = isAuthenticated && !isGuestProjectActor;

  // Hosted skills are a project-MEMBERSHIP resource (authored in Convex,
  // available even without a Computer) but gated behind the `skills-enabled`
  // PostHog flag until QA completes. Access is also enforced server-side.
  // `computersEnabled` is passed through only for the local-mode Local/Cloud
  // toggle; the skills flag applies to hosted mode only (local FS skills are
  // always available).
  if (HOSTED_MODE) {
    // Wait for the project to resolve before rendering, since hosted skills
    // have no local FS to fall back to (rendering early would hit the
    // unavailable /api/mcp/skills/* routes).
    if (!convexProjectId) {
      return null;
    }
    // Only redirect on an explicit `false`. While PostHog hydrates the flag is
    // `undefined`; bouncing then would strand a flagged-in user who cold-loads
    // /skills directly. Render nothing until it settles.
    if (skillsEnabled === false) {
      return <Navigate to={routePaths.connectors} replace />;
    }
    if (skillsEnabled === undefined) {
      return null;
    }
  }

  const skillsView = (
    <SkillsTab
      projectId={convexProjectId}
      computersEnabled={computersEnabled === true}
      // Skills over MCP (SEP-2640): the "From MCP servers" section reads its
      // catalog live, per connection, so it needs the CURRENT server list —
      // the label (host-assigned, from our registry) and whether the
      // connection is up. A disconnected server can't answer `skills/list`.
      mcpServers={skillsMcpServers}
    />
  );

  // Hosted guests keep the bare view (no peer Servers tabs to switch to) —
  // same posture as ComputerRoute. Local users ALWAYS get the Servers-page
  // chrome — the switcher is the only way back to Servers.
  if (HOSTED_MODE && !isSignedInMember) {
    return skillsView;
  }

  return (
    <motion.div
      key="skills"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={SNAPPY_RAIL}
      className="flex h-full min-h-0 flex-col"
    >
      <ConnectViewHeader
        value="skills"
        previewedHostId={previewedHostId}
        onChange={(next) => {
          if (next === "servers") {
            navigate(routePaths.connectors);
          } else if (next === "compare") {
            navigate(routePaths.hostCompare);
          } else if (next === "host" && previewedHostId) {
            navigate(buildHostsPath(previewedHostId));
          }
        }}
      />
      <div className="min-h-0 flex-1">{skillsView}</div>
    </motion.div>
  );
}

export function TasksRoute() {
  const { selectedMCPConfig, appState } = useAppRouteContext();
  return (
    <div className="h-full overflow-hidden">
      <TasksTab
        serverConfig={selectedMCPConfig}
        serverName={appState.selectedServer}
        isActive
        connectionStatus={
          appState.servers[appState.selectedServer]?.connectionStatus
        }
      />
    </div>
  );
}

export function OAuthFlowRoute() {
  const {
    appState,
    displayServerConfigs,
    areServersHydrated,
    setSelectedServer,
    saveServerConfigWithoutConnecting,
    handleConnectWithTokensFromOAuthFlow,
    handleRefreshTokensFromOAuthFlow,
    oauthServerModalNonce,
    oauthDebuggerHasHeaderServers,
  } = useAppRouteContext();

  return (
    <ErrorBoundary
      fallback={({ error, reset }) => {
        const copyDetails = () => {
          const details = formatOAuthDebuggerErrorDetails(error);
          void navigator.clipboard
            ?.writeText(details)
            .then(() => toast.success("Copied OAuth debugger error"))
            .catch(() => toast.error("Could not copy OAuth debugger error"));
        };

        return (
          <div className="flex h-full items-center justify-center p-6">
            <div className="max-w-md space-y-4 text-center">
              <AlertTriangle className="mx-auto size-10 text-destructive" />
              <div className="space-y-2">
                <h2 className="text-lg font-semibold">
                  OAuth Debugger crashed
                </h2>
                <p className="text-sm text-muted-foreground">
                  {redactOAuthDebuggerError(error).message}
                </p>
              </div>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={reset}>
                  Try again
                </Button>
                <Button variant="outline" onClick={copyDetails}>
                  Copy details
                </Button>
              </div>
            </div>
          </div>
        );
      }}
      onError={(error, errorInfo) => {
        const sanitizedError = redactOAuthDebuggerError(error);
        track("oauth_debugger_error_boundary", {
          location: "oauth_flow",
          name: sanitizedError.name,
          message: sanitizedError.message,
          stack: sanitizedError.stack,
          componentStack: redactStackLikeText(errorInfo.componentStack),
        });
      }}
    >
      <OAuthFlowTab
        serverConfigs={displayServerConfigs}
        selectedServerName={appState.selectedServer}
        hasHeaderServers={oauthDebuggerHasHeaderServers}
        areServersHydrated={areServersHydrated}
        onSelectServer={setSelectedServer}
        onSaveServerConfig={saveServerConfigWithoutConnecting}
        onConnectWithTokens={handleConnectWithTokensFromOAuthFlow}
        onRefreshTokens={handleRefreshTokensFromOAuthFlow}
        openProfileModalSignal={oauthServerModalNonce}
      />
    </ErrorBoundary>
  );
}

export function TracingRoute() {
  return <TracingTab />;
}

export function PlaygroundRoute() {
  const {
    activeHost,
    activeProject,
    activeProjectId,
    appState,
    ensureServersReady,
    evalChatHandoff,
    handleConnect,
    handleUpdateHostContext,
    isAuthenticated,
    isClientConfigSyncPending,
    isSelectedServerSyncing,
    isWorkOsLoading,
    playgroundServerSelectorProps,
    projectServers,
    remoteFirstRunOnboardingShown,
    selectedMCPConfig,
    setPlaygroundOnboarding,
    setEvalChatHandoff,
    workOsUser,
  } = useAppRouteContext();

  return (
    <PlaygroundTab
      serverConfig={selectedMCPConfig}
      serverName={appState.selectedServer}
      servers={projectServers}
      activeProjectId={activeProjectId}
      sharedProjectId={activeProject?.sharedProjectId ?? null}
      isSignedInWithWorkOs={!!workOsUser}
      isWorkOsAuthLoading={isWorkOsLoading}
      isConvexAuthenticated={isAuthenticated}
      isProjectProvisioned={Boolean(activeProject?.sharedProjectId)}
      isClientConfigSyncPending={isClientConfigSyncPending}
      hasSeenFirstRunOnboarding={remoteFirstRunOnboardingShown}
      isServerSyncing={isSelectedServerSyncing}
      onConnect={handleConnect}
      onSaveHostContext={handleUpdateHostContext}
      ensureServersReady={ensureServersReady}
      onOnboardingChange={setPlaygroundOnboarding}
      playgroundServerSelectorProps={playgroundServerSelectorProps}
      activeHost={activeHost}
      evalChatHandoff={evalChatHandoff}
      onEvalChatHandoffConsumed={(id) =>
        setEvalChatHandoff((current: EvalChatHandoff | null) =>
          current?.id === id ? null : current
        )
      }
    />
  );
}

export function SettingsRoute() {
  const { activeOrganizationId, handleNavigate } = useAppRouteContext();
  return (
    <SettingsTab
      activeOrganizationId={activeOrganizationId}
      onNavigate={handleNavigate}
    />
  );
}

export function ChatAliasRoute() {
  // Forward the query string: `/chat?conversation=<id>` is what an OAuth return
  // marker or an old bookmark can still carry, and dropping the search here
  // would land the user on an empty Playground with the id already gone.
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: routePaths.playground, search: location.search }}
      replace
    />
  );
}

export function ServersRedirectRoute() {
  return <Navigate to={routePaths.connectors} replace />;
}

export default function App() {
  const activeTab = useActiveTab();
  const [hostsTabSelectedHostId, setHostsTabSelectedHostId] = useState<
    string | null
  >(null);
  // The "active host" is unified: one selection drives the Chat tab, the
  // Servers/Playground/Hosts top-bar preview, and every MCP `initialize`
  // handshake the inspector performs. `usePreviewedHostId` is the canonical
  // storage (localStorage-backed, project-scoped) — useAppState resolves the
  // hydrated config from it internally so consumers see one source of truth.
  const [evalChatHandoff, setEvalChatHandoff] =
    useState<EvalChatHandoff | null>(null);
  const [playgroundOnboarding, setPlaygroundOnboarding] = useState(false);
  // Bumped to ask the OAuth debugger route to open its own "configure server"
  // modal instead of the generic Add Server modal — see the
  // onAddServerRequested wiring on the header server picker below.
  const [oauthServerModalNonce, setOauthServerModalNonce] = useState(0);
  const [callbackCompleted, setCallbackCompleted] = useState(false);
  const [callbackRecoveryExpired, setCallbackRecoveryExpired] = useState(false);

  // Per-tab "hide from this header" list for the OAuth / XAA debugger chip strip.
  // View-only (localStorage) — the x on a chip dismisses it from this header
  // without touching the server's config, tokens, or Convex row.
  const headerHiddenSurface: HeaderSurface | null =
    activeTab === "oauth-flow" ? "oauth" : null;
  const { hidden: hiddenHeaderServers, hide: hideHeaderServer } =
    useHiddenHeaderServers(headerHiddenSurface);

  const {
    getAccessToken,
    signIn,
    user: workOsUser,
    isLoading: isWorkOsLoading,
  } = useAuth();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthState();
  const actorKey = useActorKey();
  const currentUser = useCurrentUser(
    isAuthenticated ? ({} as any) : "skip"
  );
  // Keyed off the stored callback context rather than the platform: the
  // scenario runtime (and its OAuth flows) runs on local/desktop builds too,
  // and the completion effect below is already context-gated.
  const [hostedOAuthHandling, setHostedOAuthHandling] = useState(() => {
    const callbackContext = getHostedOAuthCallbackContext();
    return callbackContext != null && callbackContext.surface !== "project";
  });
  // Hosted mode removed: the published-scenario runtime route no longer
  // exists, so there is never a scenario chat route.
  const isScenarioChatRoute: boolean = false;

  // Chrome-less vanity surfaces (caniuse.dev, score.mcpjam.com): render
  // full-bleed without the sidebar/header, and suppress first-run onboarding
  // so a visitor lands on the thing they came for. `router.tsx` alone is not
  // enough — without this branch the score page would render inside the app
  // shell, behind the NUX redirect a guest has never seen.
  //
  // Compared with the trailing slash normalized away: the router matches
  // `/embed/score/` as happily as `/embed/score`, so an exact compare would let
  // a hand-typed or link-appended slash render this guest surface inside the
  // full shell — with the onboarding redirect live.
  const barePathname =
    window.location.pathname.length > 1
      ? window.location.pathname.replace(/\/+$/, "")
      : window.location.pathname;
  const isBareCaniuseRoute =
    barePathname === routePaths.embedHostCompare ||
    barePathname.startsWith(`${routePaths.capabilities}/`);

  const defaultHubRoute = useMemo((): "connect" | "connectors" => {
    return "connectors";
  }, []);
  const isHostedChatRoute = isScenarioChatRoute;
  const locationContext = useContext(UNSAFE_LocationContext);
  // Static operator: there is no user row to ensure (see data/user.ts).
  const isEnsuringUser = false;
  const isUserReady = true;
  const { sortedOrganizations, isLoading: isLoadingOrganizations } =
    useOrganizationQueries({ isAuthenticated });

  // Handle hosted OAuth callback: claim the callback before any hosted page renders.
  useEffect(() => {
    // Wait for Convex/WorkOS auth to settle before deciding signed-in vs guest
    // bearer. On post-redirect mount the first render sees
    // isAuthenticated=false while isAuthLoading=true; routing a signed-in
    // user's completion through the guest-bearer branch materializes a fresh
    // anonymous user with no scenarioAccess row and 403s on
    // /web/oauth/complete + /web/oauth/session/progress, then clears the
    // pending marker so the post-settle re-run can't recover.
    if (isAuthLoading) {
      return;
    }
    const callbackContext = getHostedOAuthCallbackContext();
    if (!callbackContext || callbackContext.surface === "project") {
      setHostedOAuthHandling(false);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const error = urlParams.get("error");
    const state = urlParams.get("state");
    // 2R-iss: RFC 9207 issuer identification from the callback URL.
    const iss = urlParams.get("iss");

    let cancelled = false;
    setHostedOAuthHandling(true);

    const finalizeHostedOAuth = (errorMessage?: string | null) => {
      if (cancelled) return;
      if (errorMessage && callbackContext.serverName) {
        markPendingChatScopeStepUpCancelled(
          callbackContext.serverName,
          errorMessage
        );
      }
      if (callbackContext.serverName) {
        writeHostedOAuthResumeMarker({
          surface: callbackContext.surface,
          serverName: callbackContext.serverName,
          serverUrl: callbackContext.serverUrl,
          errorMessage:
            errorMessage && errorMessage.trim() ? errorMessage : null,
        });
      }

      clearHostedOAuthPendingState();
      localStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
      localStorage.removeItem("mcp-oauth-return-hash");
      navigateApp(resolveHostedOAuthReturnPath(callbackContext), {
        replace: true,
      });
    };

    if (error || !code) {
      finalizeHostedOAuth(getHostedOAuthCallbackErrorMessage());
      setHostedOAuthHandling(false);
      return;
    }

    const handleLiveOAuthTrace = (oauthTrace: OAuthTrace) => {
      const serverId = callbackContext.serverId ?? callbackContext.serverName;
      if (!serverId) {
        return;
      }
      ingestOAuthTraceLogs({
        serverId,
        serverName: callbackContext.serverName,
        trace: oauthTrace,
      });
    };

    const hasHostedServerContext =
      !!callbackContext.projectId && !!callbackContext.serverId;
    // The fork also completed this callback for an anonymous guest (scenario
    // links, score.mcpjam.com) using a bearer minted from
    // mcpjam's hosted guest-session route. Both surfaces and the guest identity
    // itself are gone in sphinx: only a signed-in actor completes here.
    const shouldUseHostedCompletion = hasHostedServerContext && isAuthenticated;

    const completeCallback = shouldUseHostedCompletion
      ? (async () => {
          let authorizationHeader: string | undefined;
          if (workOsUser) {
            // On scenario routes, `useApiContext` is disabled (App.tsx
            // `enabled: !isHostedChatRoute`), so the module-level apiContext
            // is EMPTY_CONTEXT. authFetch's default header resolver then sees
            // `!apiContext.isAuthenticated && !apiContext.hasSession`, decides
            // the actor is a guest, and attaches a guest bearer — even though
            // the user is WorkOS-signed-in. The backend then materializes a
            // fresh anonymous user and 403s on scenarioAccess lookup. Explicitly
            // attach the WorkOS bearer here so the scenario-route gating of
            // apiContext cannot demote a signed-in user to a guest.
            try {
              const accessToken = await getAccessToken();
              if (accessToken) {
                authorizationHeader = `Bearer ${accessToken}`;
              }
            } catch {
              // Fall through to authFetch default. The callback will retry
              // via the standard error UI if this token fetch fails.
            }
          }

          return completeHostedOAuthCallback(callbackContext, code, {
            callbackState: state,
            callbackIss: iss,
            onTraceUpdate: handleLiveOAuthTrace,
            authorizationHeader,
          });
        })()
      : handleOAuthCallback(code, {
          onTraceUpdate: handleLiveOAuthTrace,
          callbackState: state,
          callbackIss: iss,
        });

    completeCallback
      .then((result) => {
        if (result.success) {
          finalizeHostedOAuth(null);
          return;
        }

        finalizeHostedOAuth(
          sanitizeHostedOAuthErrorMessage(
            result.error,
            "Authorization could not be completed. Try again."
          )
        );
      })
      .catch((callbackError) => {
        finalizeHostedOAuth(
          sanitizeHostedOAuthErrorMessage(
            callbackError,
            "Authorization could not be completed. Try again."
          )
        );
      })
      .finally(() => {
        if (!cancelled) setHostedOAuthHandling(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthLoading, isAuthenticated, workOsUser, getAccessToken]);

  const lastLaunchedActorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!actorKey) return;
    if (lastLaunchedActorRef.current === actorKey) return;
    lastLaunchedActorRef.current = actorKey;
    track("app_launched", {
      location: "app",
      user_agent: navigator.userAgent,
      version: __APP_VERSION__,
      is_authenticated: Boolean(workOsUser),
    });
  }, [actorKey, workOsUser]);

  // Set the initial theme mode and preset on page load
  const initialThemeMode = getInitialThemeMode();
  const initialThemePreset: ThemePreset = getInitialThemePreset();
  useEffect(() => {
    updateThemeMode(initialThemeMode);
    updateThemePreset(initialThemePreset);
  }, []);

  // Set up Electron OAuth callback handling
  useElectronOAuth();

  const isDebugCallback = isDebugOAuthCallbackPath(window.location.pathname);
  const isOAuthCallback = window.location.pathname === "/callback";
  const isMcpOAuthCallback = window.location.pathname === "/oauth/callback";
  // Project callbacks are completed by `useServerState`, which has its own
  // project-aware restoration path. The App-level hosted flow intentionally
  // excludes them, so its loading gate must too; otherwise this callback can
  // remain on a blank screen while that hook performs the exchange.
  const isProjectMcpOAuthCallback =
    isMcpOAuthCallback &&
    getHostedOAuthCallbackContext()?.surface === "project";
  const electronMcpCallbackUrl = buildElectronMcpCallbackUrl();

  useEffect(() => {
    if (!isOAuthCallback) {
      setCallbackCompleted(false);
      setCallbackRecoveryExpired(false);
      return;
    }

    // `/callback` without auth params after auth settled is a dead-end state.
    // Return to the shell so the user can start a fresh sign-in without
    // discarding the intended post-login destination.
    if (
      !window.location.search &&
      !isWorkOsLoading &&
      !workOsUser &&
      !isAuthLoading &&
      !isAuthenticated
    ) {
      clearHostedCallbackRetryState();
      window.history.replaceState({}, "", "/");
      setCallbackCompleted(true);
      setCallbackRecoveryExpired(false);
      return;
    }

    // Let AuthKit + Convex auth settle before leaving /callback.
    if (!isAuthLoading && isAuthenticated) {
      const scenarioReturnPath = readScenarioSignInReturnPath();
      const cliReturnPath = readCliSignInReturnPath();
      clearScenarioSignInReturnPath();
      clearCliSignInReturnPath();
      window.history.replaceState(
        {},
        "",
        scenarioReturnPath ?? cliReturnPath ?? "/"
      );
      setCallbackCompleted(true);
      setCallbackRecoveryExpired(false);
      return;
    }

    const timeout = setTimeout(() => {
      setCallbackRecoveryExpired(true);
    }, 15000);

    return () => clearTimeout(timeout);
  }, [
    isOAuthCallback,
    isAuthLoading,
    isAuthenticated,
    isWorkOsLoading,
    workOsUser,
  ]);

  const handleRetryCallbackSignIn = useCallback(() => {
    clearHostedCallbackRetryState();
    window.history.replaceState({}, "", "/");
    setCallbackCompleted(true);
    setCallbackRecoveryExpired(false);
    queueMicrotask(() => {
      signIn();
    });
  }, [signIn]);

  const handleReloadFromCallback = useCallback(() => {
    clearHostedCallbackRetryState();
    window.location.assign("/");
  }, []);

  const {
    appState,
    isLoading,
    isLoadingRemoteProjects,
    areServersHydrated,
    projectServers,
    displayServerConfigs,
    connectedOrConnectingServerConfigs,
    selectedMCPConfig,
    selectedServerEntry,
    isSelectedServerSyncing,
    handleConnect,
    handleDisconnect,
    handleRuntimeDisconnect,
    handleReconnect,
    reconnectServerForClientSwitch,
    ensureServersReady,
    ensureHostedServerIdsForNames,
    syncAgentStatus,
    handleUpdate,
    handleRemoveServer,
    setSelectedServer,
    setSelectedMCPConfigs,
    toggleServerSelection,
    projects,
    activeProjectId,
    handleSwitchProject,
    handleLeaveProject,
    handleUpdateProject,
    handleUpdateHostContext,
    handleDeleteProject,
    handleProjectShared,
    saveServerConfigWithoutConnecting,
    handleConnectWithTokensFromOAuthFlow,
    handleRefreshTokensFromOAuthFlow,
    activeOrganizationId,
    setActiveOrganizationId,
    clearConvexActiveProjectSelection,
    pendingDashboardOAuth,
    isCloudSyncActive,
    persistRuntimeServerToProjectIfNeeded,
    // Connect-screen inspector commands (`ui_add_server` & co) delegate to
    // these — the same handles the Connect screen's own buttons use.
    connectServerWithResult,
    handleDisconnect: handleDisconnectAction,
    handleRemoveServer: handleRemoveServerAction,
    activeMcpProfile,
    activeHost,
    activeHostId,
    setActiveHostId,
  } = useAppState({
    currentUserId: workOsUser?.id ?? null,
    currentActorKey: actorKey,
    hasOrganizations: sortedOrganizations.length > 0,
    isLoadingOrganizations,
    validOrganizations: sortedOrganizations,
    requestSignIn: () => {
      void signIn();
    },
  });
  const handleInspectorScopeStepUp = useCallback(
    (event: {
      serverId: string;
      requiredScope?: string;
      resourceMetadataUrl?: string;
    }) => {
      const server = resolveScopeStepUpServer(appState, {
        serverId: event.serverId,
      });
      // The harness delivers a turn's 403 out-of-band, but it is still a CHAT
      // step-up: redirecting here while the turn streams loses the transcript
      // just the same. Same queue as the stream-part channel, so whichever of
      // the two arrives second is deduped rather than doubling the redirect.
      // Outside a turn this is exactly `driveScopeStepUp`.
      driveChatScopeStepUp(server, {
        requiredScope: event.requiredScope,
        resourceMetadataUrl: event.resourceMetadataUrl,
      });
    },
    [appState]
  );
  useInspectorCommandBus({ onScopeStepUp: handleInspectorScopeStepUp });
  // One-time migration from legacy localStorage state to Convex. No-op in
  // hosted mode and after the first successful run; safe to keep in the tree.
  useLocalStateMigration({
    isAuthenticated,
    isUserBootstrapping: isAuthenticated && !isUserReady,
    organizationId: activeOrganizationId,
  });
  const oauthDebuggerServersRef = useRef(appState.servers);
  oauthDebuggerServersRef.current = appState.servers;
  const projectServersRef = useRef(projectServers);
  projectServersRef.current = projectServers;
  const selectedServerRef = useRef(appState.selectedServer);
  selectedServerRef.current = appState.selectedServer;
  const persistRuntimeServerToProjectRef = useRef(
    persistRuntimeServerToProjectIfNeeded
  );
  persistRuntimeServerToProjectRef.current =
    persistRuntimeServerToProjectIfNeeded;
  // Action-layer handles for the Connect-screen inspector commands. Refs,
  // like the ones above, so the handler-registration effect doesn't tear
  // down and re-register on every render these identities change.
  const connectServerWithResultRef = useRef(connectServerWithResult);
  connectServerWithResultRef.current = connectServerWithResult;
  const handleDisconnectRef = useRef(handleDisconnectAction);
  handleDisconnectRef.current = handleDisconnectAction;
  const handleRemoveServerRef = useRef(handleRemoveServerAction);
  handleRemoveServerRef.current = handleRemoveServerAction;
  const getInspectorServerState = useCallback((serverName: string) => {
    const runtimeServer = oauthDebuggerServersRef.current[serverName];
    const projectServer = projectServersRef.current[serverName];
    const server = runtimeServer ?? projectServer;
    return server ? { runtimeServer, projectServer, server } : null;
  }, []);
  useEffect(() => {
    return subscribeToOAuthDebuggerRequests(({ serverName }) => {
      const matchedServerName = Object.entries(
        oauthDebuggerServersRef.current
      ).find(
        ([name, server]) => name === serverName || server.name === serverName
      )?.[0];

      if (
        matchedServerName &&
        matchedServerName !== selectedServerRef.current
      ) {
        setSelectedServer(matchedServerName);
      }
    });
  }, [setSelectedServer]);
  const activeOrganizationName = sortedOrganizations.find(
    (org) => org._id === activeOrganizationId
  )?.name;
  // Hosted mode removed: the shell gate only ever gated hosted deployments,
  // so a local-only build is always "ready".
  const hostedShellGateState:
    | "ready"
    | "auth-loading"
    | "project-loading"
    | "logged-out"
    | "restricted" = "ready" as
    | "ready"
    | "auth-loading"
    | "project-loading"
    | "logged-out"
    | "restricted";
  const baseHostedShellGateState = hostedShellGateState;
  const pendingDashboardOAuthServer = pendingDashboardOAuth
    ? projectServers[pendingDashboardOAuth.serverName]
    : null;
  const shouldShowPendingDashboardOAuthGate =
    !!pendingDashboardOAuth &&
    !pendingDashboardOAuthServer &&
    baseHostedShellGateState !== "logged-out" &&
    baseHostedShellGateState !== "restricted";
  const effectiveHostedShellGateState = shouldShowPendingDashboardOAuthGate
    ? "project-loading"
    : baseHostedShellGateState;
  const hasAnyFirstRunBlockingProjectServers = Object.keys(projectServers).some(
    (serverName) => serverName !== EXCALIDRAW_SERVER_NAME
  );
  const remoteFirstRunOnboardingShown =
    currentUser == null
      ? undefined
      : currentUser.hasSeenOnboarding === true ||
        currentUser.hasCompletedOnboarding === true;
  const hasSeenFirstRunOnboarding = remoteFirstRunOnboardingShown === true;
  // A signed-in user counts as "new" (and thus gets the first-run Playground
  // redirect) only when their account was created on/after the rollout cutoff.
  // This keeps every pre-existing account on Home even if its onboarding flag
  // was never set, while still sending brand-new signups to Playground.
  const isNewSignedInAccount =
    !!workOsUser &&
    currentUser?.isAnonymous !== true &&
    typeof currentUser?.createdAt === "number" &&
    currentUser.createdAt >= FIRST_RUN_PLAYGROUND_ROLLOUT_MS;
  const isHostedDefaultRoute =
    activeTab === "connectors" || activeTab === "clients";
  const shouldHoldHostedDefaultRouteForAuth =
    HOSTED_MODE &&
    !isHostedChatRoute &&
    isHostedDefaultRoute &&
    hostedShellGateState === "auth-loading";
  // A "Verify against your server" deep-link (`/clients?template=claude`) must
  // reach HostsRoute so it can open/create that client's host. Without this
  // guard the first-run onboarding redirect below fires on the fresh load and
  // navigates to Playground, dropping the `?template` param before it's handled.
  // Only a *known* template id suppresses onboarding — an unknown/stale value
  // (e.g. `?template=bogus` from an old link) is never consumed by the deep-link
  // handler, so treating it as a real deep-link would strand new users on an
  // empty surface with onboarding silently disabled.
  const hasHostTemplateVerifyParam =
    typeof window !== "undefined" &&
    (() => {
      const raw = new URLSearchParams(window.location.search).get("template");
      return raw != null && HOST_TEMPLATES.some((t) => t.id === raw);
    })();
  // Same clobber hazard for `?project=` deep links: the onboarding redirect
  // would drop the path + param before the switch handler consumes it. Unlike
  // `?template`, membership can't be checked synchronously — but the handler
  // always strips the param once project data settles (including the
  // no-access case), so the suppression is transient by construction.
  const hasProjectSwitchDeepLinkParam =
    typeof window !== "undefined" &&
    hasProjectDeepLinkParam(window.location.search);
  const shouldRouteToFirstRunOnboarding =
    !isHostedChatRoute &&
    !isBareCaniuseRoute &&
    !hasHostTemplateVerifyParam &&
    !hasProjectSwitchDeepLinkParam &&
    !isWorkOsLoading &&
    effectiveHostedShellGateState === "ready" &&
    !(isAuthenticated && currentUser === undefined) &&
    !hasSeenFirstRunOnboarding &&
    (!HOSTED_MODE ||
      (isAuthenticated &&
        !isLoadingRemoteProjects &&
        areServersHydrated &&
        !!activeProjectId &&
        activeProjectId !== "none")) &&
    isFirstRunEligible(
      hasAnyFirstRunBlockingProjectServers,
      activeTab,
      !!workOsUser,
      remoteFirstRunOnboardingShown,
      isNewSignedInAccount
    );

  const previousConnectedServersRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const connectedServers = new Set(
      Object.entries<ServerWithName>(appState.servers)
        .filter(([, server]) => server.connectionStatus === "connected")
        .map(([name]) => name)
    );

    const previousConnectedServers = previousConnectedServersRef.current;
    const newlyConnectedServers = getNewlyConnectedServers(
      previousConnectedServers,
      connectedServers
    );

    if (activeTab === "connectors" || activeTab === "clients") {
      const firstVisitServer = newlyConnectedServers.find((serverName) => {
        try {
          return (
            localStorage.getItem(`testing-auto-opened:${serverName}`) !== "true"
          );
        } catch {
          return true;
        }
      });

      if (firstVisitServer) {
        try {
          localStorage.setItem(
            `testing-auto-opened:${firstVisitServer}`,
            "true"
          );
        } catch {
          // Ignore localStorage failures and still select the server.
        }
        setSelectedServer(firstVisitServer);
      }
    }

    previousConnectedServersRef.current = connectedServers;
  }, [activeTab, appState.servers, setSelectedServer]);

  // Auto-select a connected server when navigating to tabs that need one
  useEffect(() => {
    const needsServer =
      activeTab === "playground" ||
      activeTab === "tools" ||
      activeTab === "resources" ||
      activeTab === "prompts" ||
      activeTab === "tasks";
    if (!needsServer || selectedMCPConfig) return;

    const firstConnected = Object.entries(projectServers).find(
      ([, server]) => (server as any).connectionStatus === "connected"
    );
    if (firstConnected) {
      setSelectedServer(firstConnected[0]);
      // Playground reads `selectedMultipleServers` as the authoritative
      // selection; if we only seed `selectedServer`, the first toggle in
      // the composer popover flips the auto-selected server off because
      // the multi-set goes from `[]` → `[toggled]` and the single-server
      // fallback drops out.
      if (
        activeTab === "playground" &&
        appState.selectedMultipleServers.length === 0
      ) {
        setSelectedMCPConfigs([firstConnected[0]]);
      }
    }
  }, [
    activeTab,
    selectedMCPConfig,
    projectServers,
    setSelectedServer,
    setSelectedMCPConfigs,
    appState.selectedMultipleServers,
  ]);

  // Create effective app state that uses the correct projects (Convex when authenticated)
  const effectiveAppState = useMemo(
    () => ({
      ...appState,
      projects,
      activeProjectId,
    }),
    [appState, projects, activeProjectId]
  );

  // Get the Convex project ID from the active project
  const activeProject = projects[activeProjectId];
  const isClientConfigSyncPending =
    useProjectClientConfigSyncPending(activeProjectId);
  // Fallback chain: active named host (top-bar selection or project default
  // resolved inside useAppState) → project clientConfig shadow → SDK defaults.
  // The host is the authoritative source once `activeHost` hydrates; the
  // shadow path only matters during the bootstrap window before
  // `hostConfigsV2:getProjectDefault` returns.
  // Memoized because this feeds `useApiContext`'s dependency array. Computing
  // it inline returned a fresh object on every render whenever the host had
  // not hydrated capabilities — i.e. whenever a server was failing to connect —
  // which closed a render-speed refetch loop on /api/web/tools/list. See the
  // hook for the full chain.
  const hostedClientCapabilities = useHostedClientCapabilities(
    activeHost?.clientCapabilities,
    activeProject?.clientConfig
  );
  const convexProjectId = activeProject?.sharedProjectId ?? null;
  const projectServerConfigDto = useProjectServerConfig(
    convexProjectId ? ({ projectId: convexProjectId } as never) : "skip"
  ) as ProjectServerConfigDto | null | undefined;
  const isProjectServerConfigLoading =
    Boolean(convexProjectId) && projectServerConfigDto === undefined;
  // hostsTabSelectedHostId is a Hosts-tab-local cursor; drop it when scope
  // changes so it can't bleed across projects. `activeHostId` is owned by
  // useAppState (project-keyed in localStorage) and self-resets.
  useEffect(() => {
    if (!isAuthenticated || !convexProjectId) {
      setHostsTabSelectedHostId(null);
    }
  }, [isAuthenticated, convexProjectId]);
  useEffect(() => {
    setHostsTabSelectedHostId(null);
  }, [convexProjectId]);
  // The static operator is never an anonymous guest (see data/user.ts).
  const isGuestProjectActor = false;

  useEffect(() => {
    // A cloud-synced selection that no longer resolves to a project (deleted,
    // or a membership the user lost) must not linger as the active project.
    const hasStaleCloudProjectSelection =
      isCloudSyncActive &&
      !isLoadingOrganizations &&
      !isLoadingRemoteProjects &&
      activeProjectId !== "none" &&
      !activeProject;

    if (!hasStaleCloudProjectSelection) {
      return;
    }

    clearConvexActiveProjectSelection();
  }, [
    activeProject,
    activeProjectId,
    clearConvexActiveProjectSelection,
    isCloudSyncActive,
    isLoadingOrganizations,
    isLoadingRemoteProjects,
  ]);

  // Fetch project servers to map server IDs to names
  const { serversById } = useProjectServers({
    isAuthenticated,
    projectId: convexProjectId,
  });
  const hostedServerIdsByName = useMemo(
    () =>
      Object.fromEntries(
        Array.from(serversById.entries()).map(([id, name]) => [name, id])
      ),
    [serversById]
  );
  const oauthTokensByServerId = useMemo(
    () =>
      buildOAuthTokensByServerId(
        Object.keys(hostedServerIdsByName),
        (name) => hostedServerIdsByName[name],
        (name) => appState.servers[name]?.oauthTokens?.access_token
      ),
    [hostedServerIdsByName, appState.servers]
  );
  const hostedMcpProfilePins = useMemo(() => {
    const rawClientInfo = activeMcpProfile?.initialize?.clientInfo;
    const clientInfo =
      rawClientInfo &&
      typeof rawClientInfo === "object" &&
      !Array.isArray(rawClientInfo)
        ? rawClientInfo
        : undefined;

    const rawSupportedVersions =
      activeMcpProfile?.initialize?.supportedProtocolVersions;
    const supportedProtocolVersions =
      Array.isArray(rawSupportedVersions) && rawSupportedVersions.length > 0
        ? rawSupportedVersions.filter(
            (v): v is string => typeof v === "string" && v.trim() !== ""
          )
        : undefined;

    const rawHostPin = activeMcpProfile?.mcpProtocolVersion;
    const hostPin: McpProtocolVersion | undefined =
      typeof rawHostPin === "string" && isKnownProtocolVersion(rawHostPin)
        ? rawHostPin
        : undefined;

    const mcpProtocolVersionsByServerId: Record<string, McpProtocolVersion> =
      {};
    const seenServerIds = new Set<string>();
    for (const [serverName, serverId] of Object.entries(
      hostedServerIdsByName
    )) {
      if (seenServerIds.has(serverId)) continue;
      seenServerIds.add(serverId);
      // Project-server config is the control-plane source for per-server
      // protocol overrides. Host config mirrors it through Convex fan-out,
      // but hosted API calls should not fall back to the host default while
      // that reactive host snapshot is still catching up.
      const rawServerOverride =
        projectServerConfigDto?.overrides?.[serverId]
          ?.mcpProtocolVersionOverride ??
        activeHost?.serverConnectionOverrides?.[serverId]
          ?.mcpProtocolVersionOverride;
      const serverOverride: McpProtocolVersion | undefined =
        typeof rawServerOverride === "string" &&
        isKnownProtocolVersion(rawServerOverride)
          ? rawServerOverride
          : undefined;
      // Share the client resolver's precedence so hosted chat/eval pick up the
      // 2026 wire era a server carries via its OAuth profile / stamped config —
      // not just explicit per-server overrides. Otherwise a modal-saved 2026
      // OAuth server passes the immediate probe but hosted backend connects
      // fall back to the 2025 initialize path. (`override → config pin →
      // OAuth-profile 2026 → host default`.)
      const effective = resolveEffectiveWireProtocolVersion({
        serverConfig: undefined,
        server: appState.servers[serverName],
        serverOverride,
        hostPin,
      });
      if (!effective) continue;
      mcpProtocolVersionsByServerId[serverId] = effective;
    }

    // Enterprise-managed authorization policy — sent only when validly ON.
    // An `invalid` stored value is NOT silently dropped to off: host-bound
    // turns hit the server-authoritative 409, and interactive connects
    // fail in buildResolverConnectionDefaults with an actionable message.
    const xaaPolicyState = readXaaEnterprisePolicy(activeMcpProfile);
    const xaaPolicy =
      xaaPolicyState.kind === "on" ? xaaPolicyState.policy : undefined;

    // SEP-2243 mirroring. Host-level, so there is no per-server map to build:
    // only `"omit"` reaches the wire, as `false`.
    const mirrorToolParamHeaders =
      activeMcpProfile?.toolParamHeaderMirroring === "omit" ? false : undefined;
    // Sibling conformance knobs — same host-level shape, only the non-default
    // value reaches the wire.
    const firstPageOnly =
      activeMcpProfile?.paginationTraversal === "firstPageOnly"
        ? (true as const)
        : undefined;
    const supportsMrtr =
      activeMcpProfile?.mrtrSupport === "none" ? (false as const) : undefined;

    return {
      clientInfo,
      supportedProtocolVersions,
      mcpProtocolVersionsByServerId:
        Object.keys(mcpProtocolVersionsByServerId).length > 0
          ? mcpProtocolVersionsByServerId
          : undefined,
      mirrorToolParamHeaders,
      firstPageOnly,
      supportsMrtr,
      xaaPolicy,
    };
  }, [
    activeHost?.serverConnectionOverrides,
    activeMcpProfile,
    hostedServerIdsByName,
    projectServerConfigDto?.overrides,
    // The hosted protocol-version map now derives the OAuth-era pin from each
    // server's config/OAuth profile, so it must recompute when server state
    // changes (e.g. a modal-saved 2026 profile or a persisted config pin).
    appState.servers,
  ]);
  useApiContext({
    projectId: convexProjectId,
    serverIdsByName: hostedServerIdsByName,
    clientCapabilities: hostedClientCapabilities,
    clientInfo: hostedMcpProfilePins.clientInfo,
    supportedProtocolVersions: hostedMcpProfilePins.supportedProtocolVersions,
    mcpProtocolVersionsByServerId:
      hostedMcpProfilePins.mcpProtocolVersionsByServerId,
    mirrorToolParamHeaders: hostedMcpProfilePins.mirrorToolParamHeaders,
    firstPageOnly: hostedMcpProfilePins.firstPageOnly,
    supportsMrtr: hostedMcpProfilePins.supportsMrtr,
    xaaPolicy: hostedMcpProfilePins.xaaPolicy,
    clientConfigSyncPending:
      isClientConfigSyncPending || isProjectServerConfigLoading,
    getAccessToken,
    oauthTokensByServerId,
    // `ApiContext.isAuthenticated` means "WorkOS user is signed in",
    // not "Convex is authenticated". Convex reports authenticated for guest
    // sessions too (because `useUnifiedConvexAuth` returns a placeholder user
    // to satisfy the provider), so passing the Convex flag here makes the
    // guest-bearer fallback in `getApiAuthorizationHeader` think a real
    // user is signed in and return null. The WorkOS user object is the
    // correct signal.
    isAuthenticated: !!workOsUser,
    hasSession: !!workOsUser || isWorkOsLoading,
    enabled: !isHostedChatRoute,
  });

  const navigateToTarget = useCallback(
    (target: string, options?: { replace?: boolean }) => {
      navigateApp(navigationTargetToPath(target), options);
    },
    []
  );
  const navigateToServers = useCallback(
    (options?: { replace?: boolean }) => {
      if (window.location.pathname === routePaths.connectors) {
        return;
      }
      navigateToTarget(routePaths.connectors, options);
    },
    [navigateToTarget]
  );

  useEffect(() => {
    if (!HOSTED_MODE || !isHostedTabBlocked(activeTab)) {
      return;
    }
    toast.error(`${activeTab} is not available in hosted mode.`);
    setActiveOrganizationId(undefined);
    if (window.location.pathname !== routePaths.connectors) {
      navigateApp(routePaths.connectors, { replace: true });
    }
  }, [activeTab, setActiveOrganizationId]);

  // Registered in BOTH local and hosted modes: these handlers serve the
  // local SSE command bus (which only subscribes in local mode) AND the
  // WebMCP UI tools, which work everywhere. Hosted-blocked navigation
  // targets are rejected per the hosted tab policy instead of silently
  // falling back.
  useLayoutEffect(() => {
    const unregisterNavigate = registerInspectorCommandHandler(
      "navigate",
      async (rawCommand) => {
        const command = rawCommand as NavigateInspectorCommand;
        const rawTarget = command.payload.target?.trim() ?? "";
        if (!rawTarget) {
          throw createInspectorCommandClientError(
            "invalid_request",
            "Navigation target is empty."
          );
        }
        const firstSegment =
          rawTarget.replace(/^#/, "").replace(/^\/+/, "").split(/[/?]/)[0] ||
          "connectors";
        const tab = normalizeHostedHashTab(firstSegment);
        if (!isKnownAppTabSegment(tab)) {
          throw createInspectorCommandClientError(
            "invalid_request",
            `Unknown navigation target "${rawTarget}". Valid targets: ${listKnownAppTabSegments().join(", ")}.`
          );
        }

        navigateApp(navigationTargetToPath(rawTarget));
        await waitForUiCommit();

        // Report the tab the shell actually landed on — the caller (SSE bus)
        // plans its next step from this.
        return { activeTab: pathnameToActiveTab(window.location.pathname) };
      }
    );

    const unregisterSelectServer = registerInspectorCommandHandler(
      "selectServer",
      async (rawCommand) => {
        const command = rawCommand as SelectServerInspectorCommand;
        let serverState = getInspectorServerState(command.payload.serverName);
        if (!serverState) {
          await syncAgentStatus();
          await waitForUiCommit();
          serverState = getInspectorServerState(command.payload.serverName);
        }

        if (!serverState) {
          throw createInspectorCommandClientError(
            "unknown_server",
            `Unknown server "${command.payload.serverName}".`
          );
        }

        const connectionStatus =
          serverState.runtimeServer?.connectionStatus ??
          serverState.projectServer?.connectionStatus ??
          "disconnected";
        if (connectionStatus !== "connected") {
          throw createInspectorCommandClientError(
            "disconnected_server",
            `Server "${command.payload.serverName}" is ${connectionStatus}.`
          );
        }

        setSelectedServer(command.payload.serverName);
        await waitForUiCommit();

        return {
          selectedServer: command.payload.serverName,
          connectionStatus,
        };
      }
    );

    const unregisterOpenPlayground = registerInspectorCommandHandler(
      "openPlayground",
      async (rawCommand) => {
        const command = rawCommand as OpenPlaygroundInspectorCommand;

        if (command.payload.serverName) {
          let serverState = getInspectorServerState(command.payload.serverName);
          if (!serverState) {
            await syncAgentStatus();
            await waitForUiCommit();
            serverState = getInspectorServerState(command.payload.serverName);
          }

          if (!serverState) {
            throw createInspectorCommandClientError(
              "unknown_server",
              `Unknown server "${command.payload.serverName}".`
            );
          }

          setSelectedServer(command.payload.serverName);
          // Playground reads `selectedMultipleServers` as the authoritative
          // selection whenever it is non-empty (see PlaygroundMain /
          // PlaygroundTab). If we only set `selectedServer`, an external
          // `openPlayground` command targeting server C while the user
          // already has `[A, B]` selected lands on Playground with the
          // header focused on C but tools/LLM still scoped to A+B; and
          // the `needsServer` auto-select effect can't rescue it because
          // `selectedMCPConfig` is now set, so it early-returns. The
          // command's intent is "focus Playground on this server", so
          // replace the multi-set rather than merging.
          setSelectedMCPConfigs([command.payload.serverName]);
          const runtimeForPersist = serverState.runtimeServer;
          if (runtimeForPersist?.connectionStatus === "connected") {
            void persistRuntimeServerToProjectRef.current(
              command.payload.serverName,
              runtimeForPersist
            );
          }
        }

        navigateApp(routePaths.playground);
        await waitForUiCommit();

        return {
          activeTab: "playground",
          selectedServer:
            command.payload.serverName || selectedServerRef.current || "none",
        };
      }
    );

    // The ONE `snapshotApp` handler: app-level state only (per-surface
    // snapshot providers went with the in-app agent).
    const unregisterSnapshotApp = registerInspectorCommandHandler(
      "snapshotApp",
      async (rawCommand) => {
        const command = rawCommand as SnapshotAppInspectorCommand;
        if (command.payload != null && "surface" in command.payload) {
          throw createInspectorCommandClientError(
            "unsupported_in_mode",
            "Per-surface snapshots are not supported; omit \"surface\" for app-level state."
          );
        }

        const pathname = window.location.pathname;
        // "none" is the no-selection sentinel — don't report it as a server.
        const focused =
          selectedServerRef.current && selectedServerRef.current !== "none"
            ? selectedServerRef.current
            : undefined;
        const selectedServers = appState.selectedMultipleServers?.length
          ? appState.selectedMultipleServers
          : focused
          ? [focused]
          : [];
        return {
          path: pathname,
          activeTab: pathnameToActiveTab(pathname),
          selectedServers,
          servers: Object.entries(projectServersRef.current).map(
            ([name, server]) => ({
              name,
              connectionStatus:
                (server as { connectionStatus?: string })?.connectionStatus ??
                "unknown",
            })
          ),
        };
      }
    );

    // --- Connect-screen commands -------------------------------------
    // These delegate to the SAME action layer the Connect screen's own
    // buttons use (`use-app-state`), so an agent-added server is
    // byte-identical to a hand-added one. No DOM events, no component
    // internals — the difference between driving the app and puppeting it.

    const requireKnownServer = (serverName: string) => {
      const state = getInspectorServerState(serverName);
      if (!state) {
        throw createInspectorCommandClientError(
          "unknown_server",
          `Unknown server "${serverName}".`
        );
      }
      return state;
    };

    const readConnectionStatus = (serverName: string) => {
      const state = getInspectorServerState(serverName);
      return (
        state?.runtimeServer?.connectionStatus ??
        state?.projectServer?.connectionStatus ??
        "disconnected"
      );
    };

    // `addServer` lives in ServersTab, NOT here: creating a server has to
    // honor `BILLING_GATES.serverCreation`, and that gate is a hook that only
    // exists on the Connect screen. Registering it here would have bypassed
    // the same limit the Add button enforces. connect/disconnect/remove don't
    // create servers, so they stay App-level.

    const unregisterConnectServer = registerInspectorCommandHandler(
      "connectServer",
      async (rawCommand) => {
        const command = rawCommand as ConnectServerInspectorCommand;
        const serverName = command.payload.serverName;
        requireKnownServer(serverName);

        const result = await connectServerWithResultRef.current(serverName);
        await waitForUiCommit();

        // Report what happened, including the outcomes that are neither
        // success nor failure. `reauth` is the important one: the server
        // needs the USER to authorize it, and saying "failed" would send
        // the agent off retrying something only a human can finish.
        if (result.status === "reauth") {
          return {
            serverName,
            status: "authorization_required",
            connectionStatus: readConnectionStatus(serverName),
            message: `"${serverName}" needs authorization. The user must click Authorize on the Connect screen; it opens their identity provider.`,
          };
        }
        if (result.status === "superseded") {
          return {
            serverName,
            status: "superseded",
            message: `Another connection attempt for "${serverName}" is already in flight; leaving it alone.`,
          };
        }
        if (result.status !== "connected") {
          throw createInspectorCommandClientError(
            "execution_failed",
            result.error ?? `Could not connect "${serverName}".`
          );
        }

        return {
          serverName,
          status: "connected",
          connectionStatus: readConnectionStatus(serverName),
        };
      }
    );

    const unregisterDisconnectServer = registerInspectorCommandHandler(
      "disconnectServer",
      async (rawCommand) => {
        const command = rawCommand as DisconnectServerInspectorCommand;
        const serverName = command.payload.serverName;
        requireKnownServer(serverName);

        await handleDisconnectRef.current(serverName);
        await waitForUiCommit();
        return {
          serverName,
          connectionStatus: readConnectionStatus(serverName),
        };
      }
    );

    const unregisterRemoveServer = registerInspectorCommandHandler(
      "removeServer",
      async (rawCommand) => {
        const command = rawCommand as RemoveServerInspectorCommand;
        const serverName = command.payload.serverName;
        requireKnownServer(serverName);

        await handleRemoveServerRef.current(serverName);
        await waitForUiCommit();

        // Verify rather than assume: `handleRemoveServer` resolves void and
        // the tool result is the only thing the user will read.
        if (getInspectorServerState(serverName)) {
          throw createInspectorCommandClientError(
            "execution_failed",
            `"${serverName}" is still present after the remove.`
          );
        }
        return { serverName, removed: true };
      }
    );

    return () => {
      unregisterNavigate();
      unregisterSelectServer();
      unregisterOpenPlayground();
      unregisterSnapshotApp();
      unregisterConnectServer();
      unregisterDisconnectServer();
      unregisterRemoveServer();
    };
  }, [
    appState.selectedMultipleServers,
    getInspectorServerState,
    setSelectedServer,
    setSelectedMCPConfigs,
    syncAgentStatus,
  ]);

  useLayoutEffect(() => {
    if (shouldRouteToFirstRunOnboarding) {
      navigateApp(routePaths.playground);
    }
  }, [shouldRouteToFirstRunOnboarding]);

  // When the active project changes (org switch, project delete, manual switch),
  // snap to Servers — staying on App Builder/Chat would leave the user pointed
  // at a project that no longer exists. Start tracking only after auth/project
  // loading settles so the initial local-default → Convex-project hydration
  // doesn't yank deep-links away on first load.
  const previousActiveProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoadingRemoteProjects || isAuthLoading || isWorkOsLoading) {
      return;
    }

    // Advance the ref regardless so this project change is consumed and can't
    // trigger a stale snap on a later render (e.g. once the user leaves the
    // org route). The snap decision itself lives in a pure, unit-tested helper.
    const previousActiveProjectId = previousActiveProjectIdRef.current;
    previousActiveProjectIdRef.current = activeProjectId;
    if (
      shouldSnapToServersOnActiveProjectChange({
        previousActiveProjectId,
        nextActiveProjectId: activeProjectId,
      })
    ) {
      navigateToServers();
    }
  }, [
    activeProjectId,
    isAuthLoading,
    isLoadingRemoteProjects,
    isWorkOsLoading,
    navigateToServers,
  ]);

  useEffect(() => {
    if (activeTab === "clients" && !isAuthenticated && !isAuthLoading) {
      navigateToTarget(defaultHubRoute, { replace: true });
    } else if (activeTab === "client-config") {
      navigateToTarget(defaultHubRoute, { replace: true });
    }
  }, [
    defaultHubRoute,
    isAuthenticated,
    isAuthLoading,
    activeTab,
    navigateToTarget,
  ]);

  const handleNavigate = (section: string) => {
    navigateToTarget(section);
  };

  const handleContinueEvalInChat = useCallback(
    (handoff: Omit<EvalChatHandoff, "id">) => {
      setSelectedMCPConfigs(handoff.serverNames);
      setEvalChatHandoff({
        ...handoff,
        id: `eval-chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      navigateApp(routePaths.playground);
    },
    [setSelectedMCPConfigs]
  );

  useProjectDeepLinkSwitch({
    isAuthenticated,
    isLoadingRemoteProjects,
    projects,
    activeProjectId,
    activeOrganizationId,
    setActiveOrganizationId,
    handleSwitchProject,
  });

  const playgroundServerSelectorProps = useMemo(():
    | PlaygroundServerSelectorProps
    | undefined => {
    if (activeTab !== "playground") return undefined;
    return {
      serverConfigs: displayServerConfigs,
      selectedServer: appState.selectedServer,
      selectedMultipleServers: appState.selectedMultipleServers,
      // Playground supports multi-server selection — the user can toggle
      // several servers on simultaneously, the chat session sees their union,
      // and the docked tools pane aggregates tools across all of them.
      isMultiSelectEnabled: true,
      onServerChange: setSelectedServer,
      onMultiServerToggle: toggleServerSelection,
      onSelectMultipleServers: setSelectedMCPConfigs,
      onConnect: handleConnect,
      onReconnect: handleReconnect,
      onDisconnect: handleDisconnect,
      showOnlyOAuthServers: false,
      showOnlyServersWithViews: false,
    };
  }, [
    activeTab,
    displayServerConfigs,
    appState.selectedServer,
    appState.selectedMultipleServers,
    setSelectedServer,
    toggleServerSelection,
    setSelectedMCPConfigs,
    handleConnect,
    handleReconnect,
    handleDisconnect,
  ]);

  if (isDebugCallback) {
    return <OAuthDebugCallback />;
  }

  if (electronMcpCallbackUrl) {
    return (
      <OAuthDesktopReturnNotice returnToElectronUrl={electronMcpCallbackUrl} />
    );
  }

  if (hostedOAuthHandling) {
    return <LoadingScreen />;
  }

  // MCP OAuth completion/reconnect is handled by useServerState above. Keep
  // the app shell hidden until that effect restores the exact saved route so
  // the Servers tab never flashes between the authorization server and chat.
  if (isMcpOAuthCallback && !isProjectMcpOAuthCallback) {
    return <LoadingScreen />;
  }

  if (isOAuthCallback && !callbackCompleted) {
    if (callbackRecoveryExpired) {
      return (
        <div
          className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
          data-testid="callback-auth-timeout"
        >
          <p className="text-sm text-muted-foreground">
            Sign-in is taking longer than expected.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              onClick={handleRetryCallbackSignIn}
            >
              Try sign in again
            </button>
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm font-medium"
              onClick={handleReloadFromCallback}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return <LoadingScreen />;
  }

  if (isLoading && !isHostedChatRoute) {
    return <LoadingScreen />;
  }

  if (shouldHoldHostedDefaultRouteForAuth) {
    return <LoadingScreen />;
  }

  if (
    !isHostedChatRoute &&
    isAuthenticated &&
    (currentUser === undefined || (currentUser === null && isEnsuringUser))
  ) {
    return <LoadingScreen />;
  }

  if (!isHostedChatRoute && isAuthenticated && currentUser === null) {
    return <UserSetupError />;
  }

  const shouldShowActiveServerSelector =
    activeTab === "tools" ||
    activeTab === "resources" ||
    activeTab === "prompts" ||
    activeTab === "tasks" ||
    activeTab === "oauth-flow" ||
    activeTab === "chat";

  const oauthDebuggerHasHeaderServers = hasDebuggerHeaderServers({
    serverConfigs: projectServers,
    hiddenServers: hiddenHeaderServers,
  });

  const activeServerSelectorProps: ActiveServerSelectorProps | undefined =
    shouldShowActiveServerSelector
      ? {
          // Stays on projectServers (NOT displayServerConfigs): the header
          // picker also drives the OAuth Debugger tab, and tests explicitly
          // guard against surfacing runtime-only entries there
          // (cross-project / cross-org leak prevention).
          serverConfigs: projectServers,
          selectedServer: appState.selectedServer,
          onServerChange: setSelectedServer,
          onConnect: handleConnect,
          onReconnect: handleReconnect,
          // The OAuth debugger adds servers through its own purpose-built
          // modal, not the generic Add Server modal. Route the header "Add
          // Server" click to the debugger's modal so it matches the in-canvas
          // "Configure" button; other tabs fall back to the generic modal.
          onAddServerRequested:
            activeTab === "oauth-flow"
              ? () => setOauthServerModalNonce((n) => n + 1)
              : undefined,
          isMultiSelectEnabled: activeTab === "chat",
          onMultiServerToggle: toggleServerSelection,
          selectedMultipleServers: appState.selectedMultipleServers,
          showOnlyOAuthServers: activeTab === "oauth-flow",
          // Only the OAuth debugger header gets the "hide from this tab" x
          // button; other surfaces omit onHideServer so no x renders.
          hiddenServers: hiddenHeaderServers,
          onHideServer: headerHiddenSurface ? hideHeaderServer : undefined,
          // Debugger tab: if the header has an eligible target and NOTHING is
          // selected yet, select it instead of leaving the canvas in a
          // prompt-less empty state — but never replace an existing selection,
          // since the debugger fires live auth requests at its target and it
          // must not change without an explicit click. Other tabs keep full
          // auto-select (stale selections get replaced).
          autoSelectFilteredServer:
            activeTab === "oauth-flow" ? "when-empty" : true,
          showOnlyServersWithViews: false,
          hasMessages: false,
        }
      : undefined;

  const isEvalsTab = activeTab === "evals";
  const globalHostBarProps =
    isAuthenticated &&
    convexProjectId &&
    !isEvalsTab &&
    // The playground has its own client chip in the chat-input toolbar
    // (switch / compare / add host), so the global host bar is redundant
    // there.
    activeTab !== "playground" &&
    // The OAuth debugger targets a specific server via its own server
    // picker; the global host/client bar is irrelevant there.
    activeTab !== "oauth-flow" &&
    // Settings and the Queue are not client-scoped; no client picker there.
    activeTab !== "settings" &&
    activeTab !== "queue"
      ? {
          projectId: convexProjectId,
          onEditHost: (hostId: string) => {
            setHostsTabSelectedHostId(hostId);
            navigateApp(buildHostsPath(hostId));
          },
          // Active whenever the clients tab is mounted — the URL is the
          // source of truth for which host the canvas renders, so every
          // dropdown/cycle change must push `/clients/<hostId>`. Without
          // this, bare `/clients` (no `:hostId`) renders the cached
          // `previewedHostId` and clicking a different host only updates
          // the preview store, leaving the canvas stuck on the original.
          onCanvasReplaceHost:
            activeTab === "clients"
              ? (hostId: string) => {
                  setHostsTabSelectedHostId(hostId);
                  navigateApp(buildHostsPath(hostId), { replace: true });
                }
              : undefined,
        }
      : undefined;

  const routeContext: AppRouteContext = {
    activeMcpProfile,
    activeOrganizationId,
    activeOrganizationName,
    activeProject,
    activeProjectId,
    appState,
    activeHost,
    activeHostId,
    connectedOrConnectingServerConfigs,
    displayServerConfigs,
    convexProjectId,
    defaultHubRoute,
    ensureServersReady,
    evalChatHandoff,
    handleConnect,
    handleConnectWithTokensFromOAuthFlow,
    handleContinueEvalInChat,
    handleDeleteProject,
    handleDisconnect,
    handleLeaveProject,
    handleNavigate,
    handleProjectShared,
    handleReconnect,
    handleRuntimeDisconnect,
    handleRefreshTokensFromOAuthFlow,
    handleRemoveServer,
    handleUpdate,
    handleUpdateHostContext,
    handleUpdateProject,
    hostsTabSelectedHostId,
    isAuthLoading,
    isAuthenticated,
    isClientConfigSyncPending,
    isGuestProjectActor,
    isLoadingRemoteProjects,
    areServersHydrated,
    isSelectedServerSyncing,
    isWorkOsLoading,
    navigateToTarget,
    pendingDashboardOAuth,
    playgroundServerSelectorProps,
    projectServers,
    projects,
    remoteFirstRunOnboardingShown,
    saveServerConfigWithoutConnecting,
    selectedMCPConfig,
    selectedServerEntry,
    setPlaygroundOnboarding,
    setActiveHostId,
    setEvalChatHandoff,
    setHostsTabSelectedHostId,
    setSelectedMCPConfigs,
    setSelectedServer,
    toggleServerSelection,
    workOsUser,
    oauthDebuggerHasHeaderServers,
    oauthServerModalNonce,
  };

  const appContent = (
    <SidebarProvider defaultOpen={true}>
      <AppChromeSidebar
        hidden={playgroundOnboarding}
        onNavigate={handleNavigate}
        activeTab={activeTab}
      />
      <SidebarInset className="flex flex-col min-h-0">
        <AppChromeHeader
          hidden={playgroundOnboarding}
          activeServerSelectorProps={activeServerSelectorProps}
          globalHostBarProps={globalHostBarProps}
        />
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <AppRouteReactContext.Provider value={routeContext}>
            {locationContext ? (
              <Outlet context={routeContext} />
            ) : (
              <NoRouterRouteBody activeTab={activeTab} />
            )}
          </AppRouteReactContext.Provider>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );

  // Vanity-domain caniuse.dev pages: render the matched route full-bleed
  // without the sidebar/header chrome.
  // Still nested inside every provider in the return below, so auth, project,
  // and the guest session resolve exactly as on the normal route.
  const bareCompareContent = (
    <div className="flex h-[100dvh] min-w-0 max-w-full flex-col overflow-hidden bg-background">
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        <AppRouteReactContext.Provider value={routeContext}>
          {locationContext ? (
            <Outlet context={routeContext} />
          ) : (
            <NoRouterRouteBody activeTab={activeTab} />
          )}
        </AppRouteReactContext.Provider>
      </div>
    </div>
  );

  return (
    <PreferencesStoreProvider
      themeMode={initialThemeMode}
      themePreset={initialThemePreset}
    >
      <ProjectClientConfigSync
        activeProjectId={activeProjectId}
        savedClientConfig={activeProject?.clientConfig}
      />
      <AppStateProvider appState={effectiveAppState}>
        <ServerActionsProvider
          actions={{
            ensureServersReady,
            ensureHostedServerIdsForNames,
            runtimeDisconnectServer: handleRuntimeDisconnect,
            reconnectServer: reconnectServerForClientSwitch,
            setSelectedServerNames: setSelectedMCPConfigs,
          }}
        >
          <ActiveHostServerReconciler
            projectId={convexProjectId}
            isAuthenticated={isAuthenticated}
            activeHost={activeHost}
            activeHostId={activeHostId}
          />
          <AppReadyProvider
            isLoadingAppState={isLoading}
            isConvexAuthLoading={isAuthLoading}
            isConvexAuthenticated={isAuthenticated}
            effectiveActiveProjectId={activeProjectId}
            isLoadingRemoteProjects={isLoadingRemoteProjects}
          >
            <Toaster />
            <div data-testid="app-shell">
              {isBareCaniuseRoute ? bareCompareContent : appContent}
            </div>
          </AppReadyProvider>
        </ServerActionsProvider>
      </AppStateProvider>
    </PreferencesStoreProvider>
  );
}
